import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import { useOrgAdminAccess } from "hooks/useOrgAdminAccess";
import {
  SEVERITY_OPTIONS,
  getSessionAnnotationContext,
  requestCandidateReview,
  submitTeacherAnnotation,
} from "services/fde/annotationService";
import { logSessionReplayAccessSafely } from "services/fde/auditService";
import { getReconciledMedications } from "services/fde/reconciledMedicationsService";
import { isModule4CompleteForTci, PILOT_MODULE4_TCIS } from "services/fde/roleOnboardingService";
import { HELP_VIDEO_WORKFLOWS } from "services/helpVideoService";
import { useRegisterHelpWorkflow } from "hooks/useRegisterHelpWorkflow";
import { db } from "services/firebase";
import { formatSessionTargetLabel } from "pages/dashboard/sessions/sessionDisplayLabels";

const DEFAULT_CHECKS = [
  { id: "incomplete_followup", label: "Stopped before the topic was fully explored" },
  { id: "missed_red_flag", label: "Failed to follow up on a clinically significant detail" },
  { id: "goal_achieved", label: "Conversation achieved its intended purpose" },
];

const emptyTurnAnnotation = () => ({
  failureModeIndexes: [],
  notes: "",
  suggestedAlternative: "",
});

const SessionReplayPage = () => {
  useRegisterHelpWorkflow(HELP_VIDEO_WORKFLOWS.FDEC_TEACHER_ANNOTATION);
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const { isFdeC, fdeCTciIds } = useFdeRole();
  const { canAccess: isOrgAdmin } = useOrgAdminAccess();
  const { userData } = useAuth();
  const isTeacherOrAdmin = ["admin", "teacher"].includes(
    userData?.activeOrgRole?.role || userData?.role,
  );
  const canAccessPage = isOrgAdmin || isTeacherOrAdmin || isFdeC;
  const [session, setSession] = useState(null);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requestingCandidate, setRequestingCandidate] = useState(false);
  const [checks, setChecks] = useState([]);
  const [mustNotDoIndexes, setMustNotDoIndexes] = useState([]);
  const [selectedTurnIndex, setSelectedTurnIndex] = useState(null);
  const [turnAnnotations, setTurnAnnotations] = useState({});
  const [reconciledMedications, setReconciledMedications] = useState(null);
  const [reconciledLoadError, setReconciledLoadError] = useState("");

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      notes: "",
      severity: "medium",
      suggestedAlternative: "",
      recommendPromptRevision: false,
    },
  });

  const severity = watch("severity");

  const handleCandidateReviewResult = (result) => {
    if (result?.status === "deferred" && result?.reason === "no-active-tci-manager") {
      setWarningMessage(
        "Clinical feedback was saved, but prompt candidate generation is waiting for a TCI Manager to be assigned to this domain. Your institution admin has been notified.",
      );
      return true;
    }
    return false;
  };

  const loadSession = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const snap = await db.collection("conversationSessions").doc(sessionId).get();
      const sessionRow = snap.exists ? { id: snap.id, ...snap.data() } : null;
      setSession(sessionRow);

      if (sessionRow) {
        const ctx = await getSessionAnnotationContext(sessionId);
        setContext(ctx);
        // PHI access logging is fire-and-forget so a logging failure never blocks
        // the clinician, but the wrapper surfaces any failure to Sentry rather
        // than swallowing it (see logSessionReplayAccessSafely).
        logSessionReplayAccessSafely(sessionId, "session-replay");

        if (sessionRow.tciId === "medrec-intake") {
          try {
            const meds = await getReconciledMedications(sessionId);
            setReconciledMedications(meds?.reconciledMedications || meds || null);
            setReconciledLoadError("");
          } catch (medErr) {
            setReconciledMedications(null);
            setReconciledLoadError(
              medErr?.message || "Could not load reconciled medications.",
            );
          }
        } else {
          setReconciledMedications(null);
          setReconciledLoadError("");
        }
      }
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load session.");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const mustNotDoItems = context?.mustNotDo || [];

  const canAnnotate = useMemo(() => {
    if (!session) return false;
    if (!session.tciId) return true;
    if (isFdeC) return fdeCTciIds.includes(session.tciId);
    return true;
  }, [session, isFdeC, fdeCTciIds]);

  const selectedTurnAnnotation =
    selectedTurnIndex != null
      ? turnAnnotations[selectedTurnIndex] || emptyTurnAnnotation()
      : emptyTurnAnnotation();

  const updateTurnField = (field, value) => {
    if (selectedTurnIndex == null) return;
    setTurnAnnotations((prev) => ({
      ...prev,
      [selectedTurnIndex]: {
        ...(prev[selectedTurnIndex] || emptyTurnAnnotation()),
        [field]: value,
      },
    }));
  };

  const toggleTurnMustNotDo = (index) => {
    const current = selectedTurnAnnotation.failureModeIndexes || [];
    const next = current.includes(index)
      ? current.filter((item) => item !== index)
      : [...current, index];
    updateTurnField("failureModeIndexes", next);
  };

  const onSubmit = async (values) => {
    if (!sessionId) return;
    setSubmitting(true);
    setSuccessMessage("");
    setWarningMessage("");
    setErrorMessage("");
    try {
      const turnPayload = Object.entries(turnAnnotations).map(([turnIndex, row]) => ({
        turnIndex: Number(turnIndex),
        failureModeIndexes: row.failureModeIndexes || [],
        notes: row.notes || "",
        suggestedAlternative: row.suggestedAlternative || "",
      }));

      const result = await submitTeacherAnnotation({
        sessionId,
        severity: values.severity,
        turnAnnotations: turnPayload,
        sessionAnnotation: {
          checks,
          mustNotDoIndexes,
          notes: values.notes || "",
          severity: values.severity,
          suggestedAlternative: values.suggestedAlternative || "",
          overallAssessment: "needs_improvement",
          recommendPromptRevision: values.recommendPromptRevision === true,
        },
      });

      if (values.recommendPromptRevision) {
        const reviewResult = await requestCandidateReview({
          annotationId: result.annotationId,
          institutionId: organizationId,
          tciId: session.tciId,
          targetPromptId: session.promptIds?.[0] || "unknown",
          targetScope: "org",
          targetScopeId: organizationId,
        });
        if (handleCandidateReviewResult(reviewResult)) {
          await loadSession();
          return;
        }
      }

      if (result?.nudge?.nudged) {
        setSuccessMessage(
          "Annotation submitted. You have enough feedback to request a candidate review.",
        );
      } else if (values.severity === "critical") {
        setSuccessMessage("Annotation submitted. TCI Manager notified (critical severity).");
      } else {
        setSuccessMessage("Annotation submitted.");
      }

      await loadSession();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to submit annotation.");
    } finally {
      setSubmitting(false);
    }
  };

  const onRequestCandidate = async () => {
    if (!session || !organizationId) return;
    setRequestingCandidate(true);
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
    try {
      const annotationsSnap = await db
        .collection("teacherAnnotations")
        .where("sessionId", "==", sessionId)
        .where("status", "==", "submitted")
        .limit(1)
        .get();

      const annotationId = annotationsSnap.docs[0]?.id;
      if (!annotationId) {
        setErrorMessage("Submit an annotation before requesting candidate review.");
        return;
      }

      const reviewResult = await requestCandidateReview({
        annotationId,
        institutionId: organizationId,
        tciId: session.tciId,
        targetPromptId: session.promptIds?.[0] || "unknown",
        targetScope: "org",
        targetScopeId: organizationId,
      });
      if (!handleCandidateReviewResult(reviewResult)) {
        setSuccessMessage("Candidate review requested.");
      }
      await loadSession();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to request candidate review.");
    } finally {
      setRequestingCandidate(false);
    }
  };

  if (loading) return <Loading page />;

  if (!canAccessPage) {
    return (
      <Alert severity="warning">
        Session replay is available to Clinical Annotator, teacher, or organization admin roles.
      </Alert>
    );
  }

  if (!session) {
    return <Typography color="text.secondary">Session not found.</Typography>;
  }

  if (!canAnnotate) {
    return (
      <Alert severity="warning">
        This session is tagged for a TCI outside your Clinical Annotator assignment.
      </Alert>
    );
  }

  return (
    <Box data-testid="fdec-session-replay">
      <DashboardPageHeader
        title="Session replay"
        subtitle={formatSessionTargetLabel(session.conversationTarget)}
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}
      {warningMessage && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarningMessage("")}>
          {warningMessage}
        </Alert>
      )}

      {isFdeC &&
        session?.tciId &&
        PILOT_MODULE4_TCIS.includes(session.tciId) &&
        !isModule4CompleteForTci(userData, session.tciId) && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <LoadingButton
                size="small"
                onClick={() => navigate(`/dashboard/training-decks/${session.tciId}`)}
              >
                Open Module 4
              </LoadingButton>
            }
          >
            Soft readiness: Module 4 for {session.tciId} is not marked complete yet. You can still
            annotate this session.
          </Alert>
        )}

      {(context?.mustDo?.length > 0 || context?.mustNotDo?.length > 0) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Governance checklist
          </Typography>
          {context?.mustDo?.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Must do</Typography>
              <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                {context.mustDo.map((item) => (
                  <Typography component="li" variant="body2" key={item}>
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
          {context?.mustNotDo?.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Must not do</Typography>
              <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                {context.mustNotDo.map((item) => (
                  <Typography component="li" variant="body2" key={item}>
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      )}

      {(reconciledMedications || reconciledLoadError) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Reconciled medications
          </Typography>
          {reconciledLoadError ? (
            <Alert severity="warning">{reconciledLoadError}</Alert>
          ) : (
            <Stack spacing={0.5}>
              {(Array.isArray(reconciledMedications)
                ? reconciledMedications
                : reconciledMedications?.medications ||
                  reconciledMedications?.entries ||
                  []
              ).map((med, index) => (
                <Typography variant="body2" key={med.id || med.name || index}>
                  {med.name || med.medicationName || "Medication"}
                  {med.dose || med.dosage ? ` — ${med.dose || med.dosage}` : ""}
                  {med.frequency ? ` · ${med.frequency}` : ""}
                </Typography>
              ))}
              {!Array.isArray(reconciledMedications) &&
                !reconciledMedications?.medications &&
                !reconciledMedications?.entries && (
                  <Typography variant="body2" color="text.secondary">
                    Reconciled list loaded (see session artifact).
                  </Typography>
                )}
            </Stack>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={1}>
          {(session.turns || []).map((turn, index) => (
            <Box
              key={index}
              data-testid={`fdec-session-turn-${index}`}
              onClick={() => setSelectedTurnIndex(index)}
              sx={{
                p: 1,
                borderRadius: 1,
                cursor: "pointer",
                bgcolor: selectedTurnIndex === index ? "action.selected" : "transparent",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Turn {index + 1} · {turn.speaker}
              </Typography>
              <Typography variant="body2">{turn.text}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      <Collapse in={selectedTurnIndex != null}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Turn {selectedTurnIndex != null ? selectedTurnIndex + 1 : ""} annotation
          </Typography>
          {mustNotDoItems.length > 0 && (
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              {mustNotDoItems.map((item, index) => (
                <FormControlLabel
                  key={item}
                  control={
                    <Checkbox
                      checked={selectedTurnAnnotation.failureModeIndexes.includes(index)}
                      onChange={() => toggleTurnMustNotDo(index)}
                    />
                  }
                  label={item}
                />
              ))}
            </Stack>
          )}
          <Stack spacing={2}>
            <TextField
              label="Turn notes"
              multiline
              minRows={2}
              value={selectedTurnAnnotation.notes}
              onChange={(event) => updateTurnField("notes", event.target.value)}
            />
            <TextField
              label="Suggested alternative for this turn"
              multiline
              minRows={2}
              value={selectedTurnAnnotation.suggestedAlternative}
              onChange={(event) =>
                updateTurnField("suggestedAlternative", event.target.value)
              }
            />
          </Stack>
        </Paper>
      </Collapse>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Session feedback
        </Typography>

        {mustNotDoItems.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Session-level must-not-do flags</Typography>
            {mustNotDoItems.map((item, index) => (
              <FormControlLabel
                key={`session-${item}`}
                control={
                  <Checkbox
                    checked={mustNotDoIndexes.includes(index)}
                    onChange={(event) => {
                      setMustNotDoIndexes((prev) =>
                        event.target.checked
                          ? [...prev, index]
                          : prev.filter((value) => value !== index),
                      );
                    }}
                  />
                }
                label={item}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1} sx={{ mb: 2 }}>
          {DEFAULT_CHECKS.map((item) => (
            <FormControlLabel
              key={item.id}
              control={
                <Checkbox
                  checked={checks.includes(item.id)}
                  onChange={(event) => {
                    setChecks((prev) =>
                      event.target.checked
                        ? [...prev, item.id]
                        : prev.filter((id) => id !== item.id),
                    );
                  }}
                />
              }
              label={item.label}
            />
          ))}
        </Stack>

        <Stack component="form" spacing={2} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="severity"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="severity-label">Severity</InputLabel>
                <Select {...field} labelId="severity-label" label="Severity">
                  {SEVERITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          {severity === "critical" && (
            <Alert severity="warning">
              Critical severity notifies your TCI Manager immediately, regardless of nudge
              threshold.
            </Alert>
          )}
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Session notes" multiline minRows={3} />
            )}
          />
          <Controller
            name="suggestedAlternative"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="What should the AI have said?"
                multiline
                minRows={3}
              />
            )}
          />
          <Controller
            name="recommendPromptRevision"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Flag for prompt revision (legacy teacher flow)"
              />
            )}
          />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={submitting}
              data-testid="fdec-submit-annotation"
            >
              Submit annotation
            </LoadingButton>
            {context?.canRequestCandidate && (
              <LoadingButton
                variant="outlined"
                loading={requestingCandidate}
                onClick={onRequestCandidate}
              >
                Request candidate review
              </LoadingButton>
            )}
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default SessionReplayPage;
